import { normalizeBadgeTone } from "../badgeStyles";

export const EMPTY_VALUE = "Not recorded";

export const isPresent = (value) => {
  if (value == null) return false;
  if (typeof value === "string") return value.trim() !== "";
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "number") return !Number.isNaN(value);
  if (typeof value === "object") return Object.keys(value).length > 0;
  return true;
};

export const formatSummaryLabel = (value) => {
  if (!isPresent(value)) return "";
  return String(value)
    .trim()
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word, index) =>
      index === 0
        ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        : word.toLowerCase(),
    )
    .join(" ");
};

const OVERALL_STATUS_TONE = {
  all_good: "success",
  good: "success",
  healthy: "success",
  on_track: "info",
  needs_attention: "warning",
  watch: "warning",
  pending: "warning",
  critical: "danger",
  high_risk: "danger",
};

const RISK_LEVEL_TONE = {
  low: "success",
  medium: "warning",
  moderate: "warning",
  high: "danger",
  critical: "danger",
};

/** Barthel badge RAG from card_summary.barthel_badge (API) */
const BARTHEL_RAG_TONE = {
  red: "danger",
  amber: "warning",
  green: "success",
  grey: "neutral",
};

/** Fallback when rag is missing */
const BARTHEL_LABEL_TONE = {
  high: "danger",
  medium: "warning",
  low: "success",
  "no score": "neutral",
};

export const getOverallStatusTone = (status) => {
  const key = normalizeBadgeTone(status || "");
  return OVERALL_STATUS_TONE[key] || "neutral";
};

export const getRiskLevelTone = (level) => {
  const key = normalizeBadgeTone(level || "");
  return RISK_LEVEL_TONE[key] || "warning";
};

export const getBarthelBadgeTone = (rag, label) => {
  const ragKey = normalizeBadgeTone(rag || "");
  if (BARTHEL_RAG_TONE[ragKey]) return BARTHEL_RAG_TONE[ragKey];
  const labelKey = normalizeBadgeTone(label || "");
  return BARTHEL_LABEL_TONE[labelKey] || "neutral";
};

const parseBarthelScore = (value) => {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isNaN(n) ? null : n;
};

const resolveBarthelBadge = (summary) => {
  const raw =
    summary?.barthel_badge && typeof summary.barthel_badge === "object"
      ? summary.barthel_badge
      : null;
  if (!raw) return null;

  const label = isPresent(raw.label) ? String(raw.label).trim() : null;
  if (!label) return null;

  return {
    score: parseBarthelScore(raw.score),
    maxScore: parseBarthelScore(raw.max_score) ?? 20,
    category: isPresent(raw.category) ? String(raw.category).trim() : null,
    label,
    rag: isPresent(raw.rag) ? String(raw.rag).trim().toLowerCase() : null,
  };
};

/** Visible text on the resident card Barthel pill */
export const formatBarthelBadgeLabel = (badge) => {
  if (!badge?.label) return null;

  const isNoScore = normalizeBadgeTone(badge.label) === "no-score";
  if (isNoScore || badge.score == null) {
    return badge.label;
  }

  const max = badge.maxScore ?? 20;
  return `${badge.label} · ${badge.score}/${max}`;
};

const buildBarthelBadgeTooltip = (badge) => {
  if (!badge) return "Barthel Index";
  const parts = ["Barthel Index"];
  if (badge.category) parts.push(badge.category);
  if (badge.score != null && badge.maxScore != null) {
    parts.push(`${badge.score}/${badge.maxScore}`);
  } else if (badge.label) {
    parts.push(badge.label);
  }
  return parts.join(" · ");
};

const pickCount = (value) =>
  typeof value === "number" && !Number.isNaN(value) ? value : null;

const CRITICAL_SAFETY_KEYS = ["dnacpr", "allergies", "high_risk"];

const formatCriticalSafetyStatus = (status) => {
  if (!isPresent(status)) return null;
  const str = String(status).trim();
  if (str.includes(" ") && !str.includes("_")) return str;
  return formatSummaryLabel(str);
};

const getCriticalSafetyChipTone = (key, status, hasData) => {
  if (hasData !== true) return "neutral";

  const normalized = String(status || "")
    .trim()
    .toLowerCase()
    .replace(/_/g, " ");

  if (key === "dnacpr") {
    if (normalized.includes("not in place")) return "danger";
    if (normalized === "unknown") return "warning";
    return "danger";
  }
  if (key === "allergies" || key === "high_risk") return "danger";
  return "warning";
};

/** Chips for card_summary.critical_safety (DNACPR, allergies, high risk). */
export const buildCriticalSafetyChips = (data) => {
  const criticalSafety = data?.criticalSafety;
  if (!criticalSafety || typeof criticalSafety !== "object") return [];

  return CRITICAL_SAFETY_KEYS.map((key) => {
    const item = criticalSafety[key];
    if (!item || typeof item !== "object") return null;

    const title = isPresent(item.title)
      ? String(item.title).trim()
      : formatSummaryLabel(key);
    const status = formatCriticalSafetyStatus(item.status) || EMPTY_VALUE;

    const risks = Array.isArray(item.risks)
      ? item.risks.filter(
          (r) => isPresent(r?.risk_type) || isPresent(r?.risk_notes),
        )
      : [];

    const tooltipParts = [title, status];
    risks.forEach((risk) => {
      if (isPresent(risk.risk_type)) {
        tooltipParts.push(risk.risk_type);
      }
      if (isPresent(risk.risk_notes)) {
        tooltipParts.push(risk.risk_notes);
      }
    });

    const shortText =
      key === "high_risk"
        ? truncateChipText(status, 36)
        : truncateChipText(`${title}: ${status}`, 36);

    return {
      id: `critical-safety-${key}`,
      kind: "profile",
      category: "safety",
      categoryLabel: title,
      priority: key === "high_risk" ? 5 : key === "dnacpr" ? 6 : 7,
      icon: "safety",
      text: shortText,
      tone: getCriticalSafetyChipTone(key, item.status, item.has_data),
      tooltip: tooltipParts.join(" · "),
    };
  }).filter(Boolean);
};

const truncateChipText = (value, max = 36) => {
  if (!isPresent(value)) return "";
  const str = String(value).trim();
  return str.length > max ? `${str.slice(0, max - 1)}…` : str;
};

/** Display order for critical_information (matches API keys). */
const CRITICAL_INFORMATION_CATEGORIES = [
  { id: "conditions", label: "Conditions", basePriority: 10 },
  { id: "medication", label: "Medication", basePriority: 20 },
  { id: "abilities", label: "Abilities", basePriority: 30 },
  { id: "aids", label: "Supports", basePriority: 40 },
  { id: "equipment", label: "Aids & Equipment", basePriority: 50 },
];

const buildCategoryChipText = (categoryLabel, value) => {
  if (!isPresent(value)) return categoryLabel;
  return truncateChipText(`${categoryLabel}: ${value}`, 44);
};

const pushProfileChip = (chips, chip) => {
  chips.push({
    kind: "profile",
    ...chip,
    text: buildCategoryChipText(chip.categoryLabel, chip.value),
  });
};

const formatStatusValue = (value) => {
  if (!isPresent(value)) return "";
  const str = String(value).trim();
  if ((str.includes(" ") || str.includes("–")) && str.length > 24) return str;
  return formatSummaryLabel(str);
};

const formatAbilityPrimaryStatus = (status) => {
  if (!status || typeof status !== "object") return null;
  if (isPresent(status.ability)) return formatSummaryLabel(status.ability);
  const first = Object.values(status).find((v) => isPresent(v));
  return first ? formatStatusValue(first) : null;
};

const formatMedicationLabel = (med) => {
  if (!med || typeof med !== "object") return null;
  const name = med.medication_name || med.name;
  if (!isPresent(name)) return null;
  const parts = [med.dosage, med.form].filter(Boolean);
  const dosageForm = parts.length ? ` (${parts.join(" ")})` : "";
  const schedule = [med.frequency, med.route].filter(Boolean).join(", ");
  return schedule
    ? `${name}${dosageForm} — ${schedule}`
    : `${name}${dosageForm}`;
};

const formatConditionLabel = (condition) => {
  if (!condition) return null;
  if (typeof condition === "string") return condition;
  const name = condition.condition_name || condition.name;
  if (!isPresent(name)) return null;
  return condition.status ? `${name} (${condition.status})` : name;
};

export const resolveCriticalInformation = (summary) => {
  const raw = summary?.critical_information;
  if (!raw || typeof raw !== "object") return null;

  const specialistEquipment = raw.specialist_equipment;
  let equipmentDomains = [];
  if (Array.isArray(specialistEquipment) && specialistEquipment.length > 0) {
    equipmentDomains = [{ domain: "Equipment", items: specialistEquipment }];
  } else if (
    specialistEquipment &&
    typeof specialistEquipment === "object" &&
    !Array.isArray(specialistEquipment)
  ) {
    equipmentDomains = Object.entries(specialistEquipment)
      .filter(([, items]) => Array.isArray(items) && items.length > 0)
      .map(([domain, items]) => ({
        domain: formatSummaryLabel(domain),
        items,
      }));
  }

  return {
    abilities: Array.isArray(raw.abilities) ? raw.abilities : [],
    aids: Array.isArray(raw.aids) ? raw.aids : [],
    conditions: {
      chronic: Array.isArray(raw.conditions?.chronic)
        ? raw.conditions.chronic
        : [],
      acute: Array.isArray(raw.conditions?.acute) ? raw.conditions.acute : [],
    },
    medication: {
      chronic: Array.isArray(raw.medication?.chronic)
        ? raw.medication.chronic
        : [],
      acute: Array.isArray(raw.medication?.acute) ? raw.medication.acute : [],
    },
    equipmentDomains,
  };
};

export const hasCriticalInformationContent = (info) => {
  if (!info) return false;
  if (info.abilities.length > 0 || info.aids.length > 0) return true;
  if (info.conditions.chronic.length > 0 || info.conditions.acute.length > 0) {
    return true;
  }
  if (info.medication.chronic.length > 0 || info.medication.acute.length > 0) {
    return true;
  }
  return info.equipmentDomains.length > 0;
};

/** Chips for card_summary.critical_information (priority-sorted). */
export const buildCriticalInformationChips = (data) => {
  const info = data?.criticalInformation;
  if (!hasCriticalInformationContent(info)) return [];

  const chips = [];
  const conditionsMeta = CRITICAL_INFORMATION_CATEGORIES.find(
    (c) => c.id === "conditions",
  );
  const medicationMeta = CRITICAL_INFORMATION_CATEGORIES.find(
    (c) => c.id === "medication",
  );
  const abilitiesMeta = CRITICAL_INFORMATION_CATEGORIES.find(
    (c) => c.id === "abilities",
  );
  const aidsMeta = CRITICAL_INFORMATION_CATEGORIES.find((c) => c.id === "aids");
  const equipmentMeta = CRITICAL_INFORMATION_CATEGORIES.find(
    (c) => c.id === "equipment",
  );

  ["acute", "chronic"].forEach((bucket) => {
    const bucketOffset = bucket === "acute" ? 0 : 1;
    info.conditions[bucket].forEach((condition, index) => {
      const label = formatConditionLabel(condition);
      if (!label) return;
      pushProfileChip(chips, {
        id: `critical-info-condition-${bucket}-${index}`,
        category: "conditions",
        categoryLabel: conditionsMeta.label,
        value: label,
        priority: conditionsMeta.basePriority + bucketOffset,
        tone: bucket === "acute" ? "warning" : "clinical",
        tooltip: `${formatSummaryLabel(bucket)} · ${label}`,
      });
    });
  });

  ["acute", "chronic"].forEach((bucket) => {
    const bucketOffset = bucket === "acute" ? 0 : 1;
    info.medication[bucket].forEach((med, index) => {
      const label = formatMedicationLabel(med);
      if (!label) return;
      pushProfileChip(chips, {
        id: `critical-info-medication-${bucket}-${index}`,
        category: "medication",
        categoryLabel: medicationMeta.label,
        value: med.medication_name || med.name || label,
        priority: medicationMeta.basePriority + bucketOffset,
        tone: bucket === "acute" ? "warning" : "clinical",
        tooltip: label,
      });
    });
  });

  info.abilities.forEach((ability, index) => {
    const status = ability?.status || {};
    const primary = formatAbilityPrimaryStatus(status);
    if (!isPresent(ability?.name) && !primary) return;

    const tooltipParts = [ability.name];
    Object.entries(status).forEach(([key, value]) => {
      if (isPresent(value)) {
        tooltipParts.push(
          `${formatSummaryLabel(key)}: ${formatStatusValue(value)}`,
        );
      }
    });

    pushProfileChip(chips, {
      id: `critical-info-ability-${ability.section_id || index}`,
      category: "abilities",
      categoryLabel: abilitiesMeta.label,
      value: primary || ability.name,
      priority: abilitiesMeta.basePriority + index,
      tone: "clinical",
      tooltip: tooltipParts.join(" · "),
    });
  });

  info.aids.forEach((aidGroup, groupIndex) => {
    const entries = Array.isArray(aidGroup?.entries) ? aidGroup.entries : [];
    entries.forEach((entry, entryIndex) => {
      if (!isPresent(entry?.aid_used) && !isPresent(aidGroup?.name)) return;

      const aidLabel = formatSummaryLabel(entry.aid_used) || "Aid";
      const tooltipParts = [
        aidGroup.name,
        aidLabel,
        entry.dependency_level
          ? formatSummaryLabel(entry.dependency_level)
          : null,
        entry.notes,
      ].filter(Boolean);

      pushProfileChip(chips, {
        id: `critical-info-aid-${groupIndex}-${entryIndex}`,
        category: "aids",
        categoryLabel: aidsMeta.label,
        value: aidLabel,
        priority: aidsMeta.basePriority + groupIndex + entryIndex,
        tone: entry.restraint ? "danger" : "clinical",
        tooltip: tooltipParts.join(" · "),
      });
    });
  });

  info.equipmentDomains.forEach((domainGroup, domainIndex) => {
    domainGroup.items.forEach((item, itemIndex) => {
      const label = item.label || item.equipment_key || "Equipment";
      const detail = item.service_due
        ? `Service due ${item.service_due}`
        : item.supplier || null;
      pushProfileChip(chips, {
        id: `critical-info-equipment-${domainIndex}-${itemIndex}`,
        category: "equipment",
        categoryLabel: equipmentMeta.label,
        value: label,
        priority: equipmentMeta.basePriority + domainIndex + itemIndex,
        tone: "clinical",
        tooltip: [domainGroup.domain, label, detail]
          .filter(Boolean)
          .join(" · "),
      });
    });
  });

  return chips.sort((a, b) => a.priority - b.priority);
};

/** Grouped by API category for expanded card layout. */
export const buildCriticalInformationGroups = (data) => {
  const chips = buildCriticalInformationChips(data);

  return CRITICAL_INFORMATION_CATEGORIES.map((category) => ({
    id: category.id,
    label: category.label,
    priority: category.basePriority,
    chips: chips.filter((chip) => chip.category === category.id),
  })).filter((group) => group.chips.length > 0);
};

/** Safety + critical_information (+ Barthel), sorted by priority. */
export const buildProfileChips = (data) => {
  if (!data) return [];

  const chips = [
    ...buildCriticalSafetyChips(data),
    ...buildCriticalInformationChips(data),
  ];

  const badge = data.barthelBadge;
  if (badge?.label) {
    chips.push({
      id: "barthel-profile",
      kind: "profile",
      category: "barthel",
      categoryLabel: "Barthel",
      priority: 25,
      text: truncateChipText(formatBarthelBadgeLabel(badge)),
      tone: getBarthelBadgeTone(badge.rag, badge.label),
      tooltip: buildBarthelBadgeTooltip(badge),
    });
  }

  return chips.sort((a, b) => (a.priority ?? 99) - (b.priority ?? 99));
};

/** Expanded detail lines for critical_information */
export const buildCriticalInformationDetailLines = (data) => {
  const info = data?.criticalInformation;
  if (!hasCriticalInformationContent(info)) return [];

  const lines = [];

  info.abilities.forEach((ability, index) => {
    const status = ability?.status || {};
    const parts = Object.entries(status)
      .filter(([, value]) => isPresent(value))
      .map(
        ([key, value]) =>
          `${formatSummaryLabel(key)}: ${formatStatusValue(value)}`,
      );
    if (!parts.length && !ability?.name) return;

    lines.push({
      id: `critical-info-ability-line-${index}`,
      group: "clinical",
      icon: "clinical",
      label: ability.name || "Ability",
      value: parts.join(" · ") || EMPTY_VALUE,
      tooltip: "Abilities from care plan",
    });
  });

  info.aids.forEach((aidGroup, groupIndex) => {
    const entries = Array.isArray(aidGroup?.entries) ? aidGroup.entries : [];
    entries.forEach((entry, entryIndex) => {
      const parts = [
        entry.aid_used ? formatSummaryLabel(entry.aid_used) : null,
        entry.dependency_level
          ? formatSummaryLabel(entry.dependency_level)
          : null,
        entry.restraint ? "Restraint" : null,
        entry.notes,
      ].filter(Boolean);

      lines.push({
        id: `critical-info-aid-line-${groupIndex}-${entryIndex}`,
        group: "clinical",
        icon: "clinical",
        label: aidGroup.name || "Aid",
        value: parts.join(" · ") || EMPTY_VALUE,
        tooltip: "Aids and equipment in use",
      });
    });
  });

  ["chronic", "acute"].forEach((bucket) => {
    info.conditions[bucket].forEach((condition, index) => {
      const label = formatConditionLabel(condition);
      if (!label) return;
      lines.push({
        id: `critical-info-condition-line-${bucket}-${index}`,
        group: "clinical",
        icon: "clinical",
        label: `${formatSummaryLabel(bucket)} condition`,
        value: label,
        tooltip: "Medical conditions on record",
      });
    });
  });

  ["chronic", "acute"].forEach((bucket) => {
    info.medication[bucket].forEach((med, index) => {
      const label = formatMedicationLabel(med);
      if (!label) return;
      lines.push({
        id: `critical-info-medication-line-${bucket}-${index}`,
        group: "clinical",
        icon: "clinical",
        label: `${formatSummaryLabel(bucket)} medication`,
        value: label,
        tooltip: "Prescribed medication",
      });
    });
  });

  info.equipmentDomains.forEach((domainGroup, domainIndex) => {
    domainGroup.items.forEach((item, itemIndex) => {
      const label = item.label || item.equipment_key || "Equipment";
      const detail = item.service_due
        ? `Service due ${item.service_due}`
        : item.supplier || "Provided";
      lines.push({
        id: `critical-info-equipment-line-${domainIndex}-${itemIndex}`,
        group: "clinical",
        icon: "clinical",
        label: domainGroup.domain,
        value: `${label} — ${detail}`,
        tooltip: "Specialist equipment",
      });
    });
  });

  return lines;
};

/** Top-left badge on resident listing cards (Barthel ADL dependency). */
export const getResidentCardBarthelBadge = (resident) => {
  const data = resolveResidentSummary(resident);
  const badge = data?.barthelBadge;
  if (!badge?.label) return null;

  // Prefix so the card's "High" (ADL dependency) never reads as the "High" of
  // the adjacent Risk flag — two opposite meanings of the same red word.
  const text = formatBarthelBadgeLabel(badge);

  // Trend arrow only when actually moving (improving/declining) — a declining
  // ADL score is clinically distinct from a stable one; stable/unknown stays
  // quiet so calm cards stay calm.
  const barthel = resident?.card_summary?.barthel ?? resident?.barthel;
  const trend = String(barthel?.trend ?? "").toLowerCase();
  const delta = typeof barthel?.delta === "number" ? barthel.delta : null;
  const trendSuffix =
    (trend === "declining" || trend === "improving") &&
    delta != null &&
    delta !== 0
      ? ` ${delta < 0 ? "↓" : "↑"}${Math.abs(delta)}`
      : "";

  return {
    label: text ? `ADL: ${text}${trendSuffix}` : text,
    tone: getBarthelBadgeTone(badge.rag, badge.label),
    tooltip: buildBarthelBadgeTooltip(badge),
  };
};

/** @deprecated Use getResidentCardBarthelBadge */
export const getResidentCardRiskBadge = getResidentCardBarthelBadge;

export const resolveResidentSummary = (input) => {
  let summary = null;

  if (input != null && typeof input === "object") {
    if ("card_summary" in input) {
      if (
        input.card_summary != null &&
        typeof input.card_summary === "object"
      ) {
        summary = input.card_summary;
      }
    } else {
      // Raw summary object (no resident wrapper)
      summary = input;
    }
  }

  if (!summary) return null;

  const headline = isPresent(summary.headline) ? summary.headline.trim() : null;
  const overallStatus = isPresent(summary.overall_status)
    ? summary.overall_status
    : null;
  const isAllGood = summary.is_all_good === true;
  const urgencyRank = pickCount(summary.urgency_rank);

  const risk =
    summary.risk && typeof summary.risk === "object" ? summary.risk : null;
  const riskLevel = isPresent(risk?.level) ? risk.level : null;
  const riskReasons = Array.isArray(risk?.reasons)
    ? risk.reasons.filter((item) => isPresent(item))
    : [];

  const flags = Array.isArray(summary.flags)
    ? summary.flags.filter((item) => isPresent(item))
    : [];

  const evaluations =
    summary.evaluations && typeof summary.evaluations === "object"
      ? summary.evaluations
      : null;

  const evaluationCounts = {
    pending: pickCount(evaluations?.pending_count),
    dueSoon: pickCount(evaluations?.due_soon_count),
    overdue: pickCount(evaluations?.overdue_count),
  };
  const nearestDueLabel = isPresent(evaluations?.nearest_due_label)
    ? evaluations.nearest_due_label
    : null;

  const observations =
    summary.observations && typeof summary.observations === "object"
      ? summary.observations
      : null;

  const observationCounts = {
    overdue: pickCount(observations?.overdue_count),
    dueSoon: pickCount(observations?.due_soon_count),
    totalActive: pickCount(observations?.total_active),
  };
  const nextObservationTitle = isPresent(observations?.next_title)
    ? observations.next_title
    : null;

  const roundSummary =
    summary.round_summary && typeof summary.round_summary === "object"
      ? summary.round_summary
      : null;

  const round = {
    scope: isPresent(roundSummary?.scope) ? roundSummary.scope : null,
    label: isPresent(roundSummary?.label) ? roundSummary.label : null,
    status: isPresent(roundSummary?.status) ? roundSummary.status : null,
    completed: pickCount(roundSummary?.completed),
    missed: pickCount(roundSummary?.missed),
    skipped: pickCount(roundSummary?.skipped),
    pending: pickCount(roundSummary?.pending),
    total: pickCount(roundSummary?.total),
    completionPercent: pickCount(roundSummary?.completion_percent),
    activeRoundName: isPresent(roundSummary?.active_round_name)
      ? roundSummary.active_round_name
      : null,
  };

  const aiAlerts =
    summary.ai_alerts && typeof summary.ai_alerts === "object"
      ? summary.ai_alerts
      : null;

  const aiAlertCount =
    pickCount(aiAlerts?.total_count) ?? pickCount(aiAlerts?.count) ?? null;
  const aiGovernanceCount = pickCount(aiAlerts?.governance_count);
  const aiClinicalCount = pickCount(aiAlerts?.clinical_warning_count);
  const aiByPriority = aiAlerts?.by_priority || null;
  const aiHighPriority = pickCount(aiByPriority?.high);

  const topItems = Array.isArray(aiAlerts?.top_items)
    ? aiAlerts.top_items.filter((item) => isPresent(item?.title))
    : [];

  const topItem = aiAlerts?.top_item ?? topItems[0] ?? null;
  const aiTopText =
    typeof topItem === "string"
      ? topItem.trim()
      : topItem?.title || topItem?.message || "";

  const actionItems = Array.isArray(summary.action_items)
    ? summary.action_items.filter((item) => isPresent(item?.label))
    : [];

  const aiNarrative = isPresent(summary.ai_narrative)
    ? String(summary.ai_narrative).trim()
    : null;

  const clinicalScores = Array.isArray(summary.clinical_scores)
    ? summary.clinical_scores
    : [];

  const hasBarthelField = "barthel" in summary;
  const barthel =
    summary.barthel != null && !Number.isNaN(Number(summary.barthel))
      ? Number(summary.barthel)
      : null;
  const barthelBadge = resolveBarthelBadge(summary);

  const criticalSafety =
    summary.critical_safety && typeof summary.critical_safety === "object"
      ? summary.critical_safety
      : null;

  const criticalInformation = resolveCriticalInformation(summary);

  return {
    headline,
    overallStatus,
    isAllGood,
    urgencyRank,
    riskLevel,
    riskReasons,
    flags,
    evaluationCounts,
    nearestDueLabel,
    observationCounts,
    nextObservationTitle,
    round,
    aiAlertCount,
    aiGovernanceCount,
    aiClinicalCount,
    aiHighPriority,
    aiByPriority,
    topItems,
    aiTopText: aiTopText || null,
    actionItems,
    aiNarrative,
    clinicalScores,
    hasBarthelField,
    barthel,
    barthelBadge,
    criticalSafety,
    criticalInformation,
    hasContent: true,
  };
};

const formatCountLabel = (count, label) => `${count} ${label}`;

/** Icon-backed stat segments for collapsed row */
export const buildSummaryStatsSegments = (data) => {
  if (!data) return [];

  const segments = [];
  const ev = data.evaluationCounts;

  const pushWorkload = (chip) => segments.push({ kind: "workload", ...chip });

  if (ev.pending > 0) {
    pushWorkload({
      icon: "evaluations",
      text: formatCountLabel(ev.pending, "pending"),
      tone: "alert",
      tooltip: `${ev.pending} evaluations awaiting completion`,
    });
  }
  if (ev.overdue > 0) {
    pushWorkload({
      icon: "evaluations",
      text: formatCountLabel(ev.overdue, "overdue"),
      tone: "danger",
      tooltip: `${ev.overdue} evaluations overdue`,
    });
  }
  if (data.aiAlertCount > 0) {
    let text = formatCountLabel(data.aiAlertCount, "alerts");
    if (data.aiHighPriority > 0) {
      text += ` · ${data.aiHighPriority} urgent`;
    }
    pushWorkload({
      icon: "ai",
      text,
      tone: "warning",
      tooltip: [
        `${data.aiAlertCount} AI alerts`,
        data.aiGovernanceCount != null
          ? `${data.aiGovernanceCount} governance`
          : null,
        data.aiClinicalCount != null
          ? `${data.aiClinicalCount} clinical`
          : null,
      ]
        .filter(Boolean)
        .join(" · "),
    });
  }
  if (data.round.missed > 0) {
    pushWorkload({
      icon: "rounds",
      text: formatCountLabel(data.round.missed, "missed"),
      tone: "danger",
      tooltip: `${data.round.missed} care rounds missed`,
    });
  }
  if (data.observationCounts.overdue > 0) {
    pushWorkload({
      icon: "observations",
      text: formatCountLabel(data.observationCounts.overdue, "obs overdue"),
      tone: "danger",
      tooltip: "Overdue observations",
    });
  }

  return segments;
};

/** @deprecated Use buildSummaryStatsSegments */
export const buildSummaryStatsLine = (data) => {
  const segments = buildSummaryStatsSegments(data);
  return {
    text: segments.map((s) => s.text).join(" · "),
    segments,
    highlights: segments.map((s) => s.icon),
    tooltip: segments.map((s) => s.tooltip).join(" · "),
  };
};

/** @deprecated Use buildSummaryStatsLine */
export const buildSummaryStatPills = (data) => {
  const line = buildSummaryStatsLine(data);
  if (!line.text) return [];
  return [
    { id: "stats", label: line.text, tone: "neutral", tooltip: line.tooltip },
  ];
};

export const buildSummaryPreview = (data) => {
  if (!data) return null;

  const statsSegments = buildSummaryStatsSegments(data);
  const profileChips = buildProfileChips(data);
  const criticalInformationGroups = buildCriticalInformationGroups(data);
  const previewText = data.headline || "View summary";

  const hasExpandableDetails =
    profileChips.length > 0 ||
    criticalInformationGroups.length > 0 ||
    statsSegments.length > 0 ||
    data.riskReasons.length > 0 ||
    data.actionItems.length > 0 ||
    data.topItems.length > 1 ||
    data.aiNarrative ||
    data.clinicalScores.length > 0 ||
    data.barthelBadge ||
    data.hasBarthelField ||
    data.round.label ||
    data.round.status;

  return {
    previewText,
    statsSegments,
    profileChips,
    criticalInformationGroups,
    criticalSafetyChips: buildCriticalSafetyChips(data),
    criticalInformationChips: buildCriticalInformationChips(data),
    hasExpandableDetails: Boolean(hasExpandableDetails),
    actionItems: data.actionItems,
    aiNarrative: data.aiNarrative,
  };
};

/** Flat detail lines for expanded panel (no metric grid) */
export const buildSummaryDetailLines = (data) => {
  if (!data) return [];

  const lines = [];
  const ev = data.evaluationCounts;
  const obs = data.observationCounts;
  const round = data.round;

  if (ev.pending > 0 || ev.dueSoon > 0 || ev.overdue > 0) {
    const parts = [];
    if (ev.pending > 0) parts.push(`${ev.pending} pending`);
    if (ev.dueSoon > 0) parts.push(`${ev.dueSoon} due soon`);
    if (ev.overdue > 0) parts.push(`${ev.overdue} overdue`);
    lines.push({
      id: "evaluations",
      group: "workload",
      icon: "evaluations",
      label: "Evaluations",
      value: parts.join(" · "),
      tooltip: data.nearestDueLabel
        ? `Next due: ${data.nearestDueLabel}`
        : "Evaluation workload",
    });
  }

  if (
    obs.totalActive > 0 ||
    obs.overdue > 0 ||
    obs.dueSoon > 0 ||
    data.nextObservationTitle
  ) {
    const parts = [];
    if (obs.totalActive > 0) parts.push(`${obs.totalActive} active`);
    if (obs.overdue > 0) parts.push(`${obs.overdue} overdue`);
    if (obs.dueSoon > 0) parts.push(`${obs.dueSoon} due soon`);
    lines.push({
      id: "observations",
      group: "workload",
      icon: "observations",
      label: "Observations",
      value: parts.join(" · ") || data.nextObservationTitle,
      tooltip: data.nextObservationTitle || "Scheduled observations",
    });
  }

  if (
    round.label ||
    round.status ||
    round.activeRoundName ||
    round.missed > 0 ||
    round.completed > 0
  ) {
    const parts = [];
    if (round.label) parts.push(round.label);
    else if (round.scope) parts.push(formatSummaryLabel(round.scope));
    if (round.status) parts.push(formatSummaryLabel(round.status));
    if (round.completed > 0) parts.push(`${round.completed} done`);
    if (round.missed > 0) parts.push(`${round.missed} missed`);
    if (round.completionPercent != null && round.total > 0) {
      parts.push(`${round.completionPercent}% complete`);
    }
    lines.push({
      id: "rounds",
      group: "workload",
      icon: "rounds",
      label: "Care rounds",
      value: parts.join(" · "),
      tooltip: round.activeRoundName || "Today's care rounds",
    });
  }

  if (data.aiAlertCount > 0) {
    const parts = [`${data.aiAlertCount} total`];
    if (data.aiHighPriority > 0) {
      parts.push(`${data.aiHighPriority} high priority`);
    }
    if (data.aiGovernanceCount > 0) {
      parts.push(`${data.aiGovernanceCount} governance`);
    }
    if (data.aiClinicalCount > 0) {
      parts.push(`${data.aiClinicalCount} clinical`);
    }
    lines.push({
      id: "ai",
      group: "workload",
      icon: "ai",
      label: "AI alerts",
      value: parts.join(" · "),
      tooltip: "AI-generated alerts requiring attention",
    });
  }

  if (data.clinicalScores.length > 0) {
    lines.push({
      id: "clinical",
      group: "clinical",
      icon: "clinical",
      label: "Clinical scores",
      value: `${data.clinicalScores.length} recorded`,
      tooltip: "Clinical assessment scores on file",
    });
  }

  if (data.flags.length > 0) {
    lines.push({
      id: "flags",
      group: "workload",
      icon: "flags",
      label: "Flags",
      value: data.flags.map(formatSummaryLabel).join(" · "),
      tooltip: "Active resident flags",
    });
  }

  if (data.overallStatus && !data.isAllGood) {
    lines.push({
      id: "overall",
      group: "workload",
      icon: "overall",
      label: "Overall",
      value: formatSummaryLabel(data.overallStatus),
      tooltip: "Composite status from care systems",
    });
  }

  return lines;
};

/** @deprecated */
export const buildSummarySections = (data) =>
  buildSummaryDetailLines(data).map((line) => ({
    id: line.id,
    title: line.label,
    icon: line.id,
    tooltip: line.tooltip,
    items: [
      {
        key: "value",
        label: line.label,
        value: line.value,
        tooltip: line.tooltip,
      },
    ],
  }));

export const buildSummaryPreviewChips = buildSummaryStatPills;
export const buildSummaryDetailRows = buildSummaryDetailLines;
